/**
 * Pure model of the « Tableau de bord » (direction C, validated by Pascal on
 * 26/09/2026): period arithmetic for the Semaine / Mois / Année views, source
 * freshness chips, the « À traiter » list and the money-flow steps. Nothing
 * here renders; every rule is unit-tested in dashboardModel.test.ts.
 */
import type { SourceState, Tone } from '../../components/c';
import { clientLogoSrc } from '../../components/c/ClientLogo';
import { calendarRowClient, type ItemClient } from '../shared/itemClient';
import { clientHubspotAttention, type IdentityChipState } from '../clients/clientIdentity';
import type { MyWeekItem, MyWeekPayload, MyWeekTarget } from '../../lib/api';
import { formatCurrency, formatDays, safeString, toNumber } from '../../lib/format';
import type { DashboardPayload, SourceMetadata, TabEntity } from '../../lib/types';
import { PAID_REVENUE_HT_LABEL, paidRevenueHtScope } from '../../lib/metric-contract';
import type { ViewStateFilters, ViewStateKey } from '../../store/ui';
import {
  toFacturesTarget,
  toIntegrationsTarget,
  toIntervenantsTarget,
  type BusinessOsNavigationTarget,
} from '../shared/navigation';
import {
  diagnoseSource,
  hubspotReadinessTask,
  incompleteInvoicesTarget,
  pipelineProblemTarget,
  sourceStateTitle,
  type FixAction,
  type PipelineProblem,
  type SourceConnector,
  type SourceFix,
} from '../shared/sourceFix';

/* ───────────────────────── Views and URL state ───────────────────────── */

export type DashboardView = 'semaine' | 'mois' | 'annee';

export const DASHBOARD_VIEWS: Array<{ value: DashboardView; label: string }> = [
  { value: 'semaine', label: 'Semaine' },
  { value: 'mois', label: 'Mois' },
  { value: 'annee', label: 'Année' },
];

/**
 * `?vue=` is the single source of truth; without it, or anything unknown, the
 * dashboard opens on the year (Pascal, 30/09/2026: « Peux-tu faire en sorte que
 * le dashboard s'ouvre par défaut sur l'année et non pas sur la semaine ? »).
 */
export function parseView(raw: string | null | undefined): DashboardView {
  const value = safeString(raw).trim().toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
  return value === 'semaine' || value === 'mois' ? value : 'annee';
}

/* ───────────────────────── Dates (local calendar) ───────────────────────── */

const pad = (value: number) => String(value).padStart(2, '0');

export function isoDate(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export function parseIsoDate(raw: string | null | undefined): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(safeString(raw).trim());
  if (!match) return null;
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  const date = new Date(year, month - 1, day);
  return date.getMonth() === month - 1 && date.getDate() === day ? date : null;
}

export function addDays(date: Date, days: number): Date {
  const next = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  next.setDate(next.getDate() + days);
  return next;
}

export function mondayOf(date: Date): Date {
  return addDays(date, -((date.getDay() + 6) % 7));
}

/** Today projected into the exercise (same day and month), like the calendar. */
export function todayInExercise(year: number, today: Date): Date {
  const lastDay = new Date(year, today.getMonth() + 1, 0).getDate();
  return new Date(year, today.getMonth(), Math.min(today.getDate(), lastDay));
}

export function defaultWeekStart(year: number, today: Date): string {
  return isoDate(mondayOf(todayInExercise(year, today)));
}

/** `?semaine=` holds a Monday of the selected exercise; anything else is ignored. */
export function resolveWeekStart(raw: string | null | undefined, year: number, today: Date): string {
  const date = parseIsoDate(raw);
  if (date && date.getDay() === 1 && date.getFullYear() === year) return isoDate(date);
  return defaultWeekStart(year, today);
}

export function shiftWeekStart(weekStart: string, weeks: number): string {
  const start = parseIsoDate(weekStart) || mondayOf(new Date());
  return isoDate(addDays(mondayOf(start), weeks * 7));
}

export function yearOfIso(value: string): number {
  return Number(value.slice(0, 4));
}

function dayLabel(date: Date): string {
  return date.getDate() === 1 ? '1er' : String(date.getDate());
}

function longMonth(date: Date): string {
  return date.toLocaleDateString('fr-FR', { month: 'long' });
}

/** « 21 au 27 septembre 2026 », « 28 septembre au 4 octobre 2026 »… */
export function weekRangeLabel(weekStart: string): string {
  const start = parseIsoDate(weekStart) || mondayOf(new Date());
  const end = addDays(start, 6);
  if (start.getFullYear() !== end.getFullYear()) {
    return `${dayLabel(start)} ${longMonth(start)} ${start.getFullYear()} au ${dayLabel(end)} ${longMonth(end)} ${end.getFullYear()}`;
  }
  if (start.getMonth() !== end.getMonth()) {
    return `${dayLabel(start)} ${longMonth(start)} au ${dayLabel(end)} ${longMonth(end)} ${end.getFullYear()}`;
  }
  return `${dayLabel(start)} au ${dayLabel(end)} ${longMonth(end)} ${end.getFullYear()}`;
}

export interface WeekDay {
  iso: string;
  dow: string;
  day: number;
  isToday: boolean;
  weekend: boolean;
}

export function weekDays(weekStart: string, today: Date): WeekDay[] {
  const start = parseIsoDate(weekStart) || mondayOf(today);
  const todayIso = isoDate(today);
  return Array.from({ length: 7 }, (_, index) => {
    const date = addDays(start, index);
    return {
      iso: isoDate(date),
      dow: date.toLocaleDateString('fr-FR', { weekday: 'short' }),
      day: date.getDate(),
      isToday: isoDate(date) === todayIso,
      weekend: index >= 5,
    };
  });
}

/** « 24 mai » (with the year only when it differs from the reference year). */
export function shortDate(raw: string | null | undefined, referenceYear?: number): string {
  const date = parseIsoDate(raw);
  if (!date) return '';
  const label = `${dayLabel(date)} ${longMonth(date)}`;
  return referenceYear && date.getFullYear() !== referenceYear ? `${label} ${date.getFullYear()}` : label;
}

/* ───────────────────────── Months ───────────────────────── */

export function monthKeyOf(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}`;
}

function parseMonthKey(raw: string | null | undefined): { year: number; month: number } | null {
  const match = /^(\d{4})-(\d{2})$/.exec(safeString(raw).trim());
  if (!match) return null;
  const month = Number(match[2]);
  if (month < 1 || month > 12) return null;
  return { year: Number(match[1]), month };
}

export function defaultMonthKey(year: number, today: Date): string {
  return monthKeyOf(todayInExercise(year, today));
}

/** `?mois=` holds a month of the selected exercise; anything else is ignored. */
export function resolveMonthKey(raw: string | null | undefined, year: number, today: Date): string {
  const parsed = parseMonthKey(raw);
  if (parsed && parsed.year === year) return `${parsed.year}-${pad(parsed.month)}`;
  return defaultMonthKey(year, today);
}

export function shiftMonthKey(monthKey: string, months: number): string {
  const parsed = parseMonthKey(monthKey) || { year: new Date().getFullYear(), month: 1 };
  return monthKeyOf(new Date(parsed.year, parsed.month - 1 + months, 1));
}

function monthDate(monthKey: string): Date {
  const parsed = parseMonthKey(monthKey) || { year: new Date().getFullYear(), month: 1 };
  return new Date(parsed.year, parsed.month - 1, 1);
}

/** « septembre » */
export function monthName(monthKey: string): string {
  return longMonth(monthDate(monthKey));
}

/** « Septembre 2026 » */
export function monthTitle(monthKey: string): string {
  const label = `${monthName(monthKey)} ${monthKey.slice(0, 4)}`;
  return label.charAt(0).toUpperCase() + label.slice(1);
}

/** « sept. » */
export function monthShort(monthKey: string): string {
  return monthDate(monthKey).toLocaleDateString('fr-FR', { month: 'short' });
}

export interface GridDay {
  iso: string;
  day: number;
  inMonth: boolean;
  weekend: boolean;
  isToday: boolean;
  /** Short month name printed on the first cell of a month (« sept. »). */
  monthTag: string;
}

/** Monday-first weeks covering the month, days of the adjacent months included. */
export function monthGrid(monthKey: string, today: Date): GridDay[][] {
  const first = monthDate(monthKey);
  const last = new Date(first.getFullYear(), first.getMonth() + 1, 0);
  const todayIso = isoDate(today);
  const weeks: GridDay[][] = [];
  let cursor = mondayOf(first);
  while (cursor <= last) {
    const week: GridDay[] = [];
    for (let index = 0; index < 7; index += 1) {
      const inMonth = cursor.getMonth() === first.getMonth() && cursor.getFullYear() === first.getFullYear();
      const firstCell = weeks.length === 0 && index === 0;
      week.push({
        iso: isoDate(cursor),
        day: cursor.getDate(),
        inMonth,
        weekend: index >= 5,
        isToday: isoDate(cursor) === todayIso,
        monthTag: cursor.getDate() === 1 || firstCell ? cursor.toLocaleDateString('fr-FR', { month: 'short' }) : '',
      });
      cursor = addDays(cursor, 1);
    }
    weeks.push(week);
  }
  return weeks;
}

/**
 * Same range as the Calendrier month view (previous month → month + 2), so
 * both screens share one cached request for a given month.
 */
export function calendarRangeForMonth(monthKey: string): { from: string; to: string } {
  const first = monthDate(monthKey);
  return {
    from: isoDate(new Date(first.getFullYear(), first.getMonth() - 1, 1)),
    to: isoDate(new Date(first.getFullYear(), first.getMonth() + 2, 1)),
  };
}

/* ───────────────────────── Navigation targets ───────────────────────── */

export type NavTarget = BusinessOsNavigationTarget;

const IDENTITY_PARAM: Record<string, string> = {
  '/calendrier': 'session',
  '/pipeline': 'opportunite',
  '/factures': 'facture',
  '/missions': 'mission',
  '/clients': 'client',
  '/intervenants': 'intervenant',
};

/**
 * Carries the item identity in the URL (`/factures?facture=<id>`), as the
 * funnel rule asks: the arrival screen reads its parameter at load and opens
 * THAT item. A route that already holds its parameters is left as built.
 */
export function withIdentity(target: NavTarget): NavTarget {
  const id = safeString(target.filters?.entityId).trim();
  const key = IDENTITY_PARAM[target.route];
  if (!id || !key || target.route.includes('?')) return target;
  return { ...target, route: `${target.route}?${key}=${encodeURIComponent(id)}` };
}

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * One calendar item (session or event): `/calendrier?session=<id>&date=<day>`.
 * The day is the item's first day, as the Calendrier writes it itself, so the
 * calendar opens on the period that holds the item, even outside the month it
 * shows by default. `null` when the item has no identity: the caller then
 * renders it non-interactive (never a click that goes nowhere).
 */
export function toSessionTarget(id: string, day: string): NavTarget | null {
  const sessionId = safeString(id).trim();
  if (!sessionId) return null;
  const params = new URLSearchParams({ session: sessionId });
  const iso = safeString(day).slice(0, 10);
  if (ISO_DAY.test(iso)) params.set('date', iso);
  return {
    route: `/calendrier?${params.toString()}`,
    view: 'calendrier',
    // Same month as the day: the calendar's month intent never pulls the
    // anchor away from the item.
    filters: ISO_DAY.test(iso) ? { entityId: sessionId, monthKey: iso.slice(0, 7) } : { entityId: sessionId },
  };
}

/**
 * Planification (`/affectation`): `?mois=` positions the month and `?coller=1`
 * opens the « Coller depuis Excel » panel. The view state is emptied so that
 * no month filter left by an earlier visit hides the missions of other months.
 */
export function toPlanningTarget({ monthKey = '', paste = false }: { monthKey?: string; paste?: boolean }): NavTarget {
  const params = new URLSearchParams();
  if (paste) params.set('coller', '1');
  if (/^\d{4}-(0[1-9]|1[0-2])$/.test(monthKey)) params.set('mois', monthKey);
  const query = params.toString();
  return { route: query ? `/affectation?${query}` : '/affectation', view: 'affectation', filters: {} };
}

export function fromMyWeekTarget(target: MyWeekTarget | undefined): NavTarget | null {
  if (!target || !safeString(target.route).startsWith('/')) return null;
  return {
    route: target.route,
    view: target.view as ViewStateKey,
    filters: (target.filters || {}) as ViewStateFilters,
    year: target.year,
  };
}

/** The target of a week item; a calendar session also carries its day in the URL. */
export function fromMyWeekItem(item: MyWeekItem): NavTarget | null {
  const target = fromMyWeekTarget(item.target);
  const sessionId = safeString(target?.filters?.entityId).trim();
  if (target?.route === '/calendrier' && sessionId) return toSessionTarget(sessionId, safeString(item.date));
  return target;
}

/* ───────────────────────── Sources (SourceChip) ───────────────────────── */

export interface SourceInput {
  label: string;
  meta: unknown;
  /** Fibery is the internal database: it has no synchronisation to watch. */
  internal?: boolean;
  outsideYearCount?: number | null;
  /** Connector whose action fixes the source (sync, deals or invoices to correct). */
  connector?: SourceConnector;
  year?: number;
}

export interface SourceStatus {
  label: string;
  state: SourceState;
  problem: string;
  details: string[];
}

function asMeta(value: unknown): SourceMetadata | null {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as SourceMetadata) : null;
}

function formatSyncDate(raw: unknown): string {
  const text = safeString(raw);
  if (!text) return '';
  const parsed = new Date(text);
  if (Number.isNaN(parsed.getTime())) return text;
  return parsed.toLocaleString('fr-FR', { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' });
}

const READINESS_PROBLEMS: Record<string, string> = {
  blocked_portal_unverified: 'portail à vérifier',
  blocked_non_eur_or_missing_currency: 'devise à compléter',
  blocked_missing_amounts: 'montants à compléter',
  blocked_missing_issue_dates: 'dates d’émission à compléter',
  blocked_missing_close_dates: 'dates de clôture à compléter',
  blocked_missing_probabilities: 'probabilités à compléter',
};

export function describeSource(input: SourceInput): SourceStatus {
  const meta = asMeta(input.meta);
  if (input.internal) return { label: input.label, state: 'ok', problem: '', details: [`${input.label} : base interne`] };
  if (!meta) return { label: input.label, state: 'unknown', problem: 'état inconnu', details: [] };
  const lastSync = formatSyncDate(meta.last_success_at);
  const details = [
    lastSync ? `${input.label} : synchronisé le ${lastSync}` : `${input.label} : jamais synchronisé`,
    safeString(meta.quality_warning),
    safeString(meta.last_error),
  ].filter(Boolean);
  const freshness = safeString(meta.freshness).toLowerCase();
  const readiness = safeString(meta.business_readiness);
  const retentionLimited = meta.historical_financial_complete === false
    || (typeof meta.retention_filtered_count === 'number' && meta.retention_filtered_count > 0);
  let state: SourceState = 'ok';
  let problem = '';
  if (safeString(meta.last_error) || freshness === 'degraded') {
    state = 'error';
    problem = 'en erreur';
  } else if (!safeString(meta.last_success_at) || freshness === 'never_synced') {
    state = 'unknown';
    problem = 'jamais synchronisé';
  } else if (freshness === 'stale') {
    state = 'warn';
    problem = 'copie ancienne';
  } else if (readiness.startsWith('blocked')) {
    state = 'warn';
    const outside = toNumber(input.outsideYearCount ?? (meta as Record<string, unknown>).open_deals_outside_year_count);
    problem = readiness === 'blocked_open_deals_outside_year'
      ? outside > 0 ? `${outside} date${outside > 1 ? 's' : ''} à corriger` : 'dates à corriger'
      : READINESS_PROBLEMS[readiness] || 'données à compléter';
  } else if (retentionLimited && meta.selected_period_financial_complete !== true) {
    state = 'warn';
    problem = 'historique limité';
  }
  return { label: input.label, state, problem, details };
}

const STATE_RANK: Record<SourceState, number> = { ok: 0, unknown: 1, warn: 2, error: 3 };

export interface SourceChipModel {
  key: string;
  state: SourceState;
  text: string;
  details: string[];
  /** Real cause and the single action of a pill that is not « à jour ». */
  fix?: SourceFix | null;
}

const FIX_RANK: Record<string, number> = {
  '': 0,
  history_limited: 1,
  coverage_incomplete: 2,
  configuration_missing: 3,
  source_stale: 4,
  source_not_synced: 5,
  source_error: 6,
};

function inputFix(input: SourceInput): SourceFix | null {
  if (input.internal || !input.connector) return null;
  const diagnosis = diagnoseSource(input.connector, input.meta, { year: input.year, outsideYearCount: input.outsideYearCount });
  return diagnosis.cause ? diagnosis : null;
}

function worstFix(left: SourceFix | null | undefined, right: SourceFix | null | undefined): SourceFix | null {
  if (!left) return right || null;
  if (!right) return left;
  return (FIX_RANK[right.cause] || 0) > (FIX_RANK[left.cause] || 0) ? right : left;
}

/**
 * One pill per source that needs attention, and a single green pill for all
 * the healthy ones (« Qonto · Fibery · Digiforma »), as in the validated mockup.
 */
export function buildSourceChips(inputs: SourceInput[]): SourceChipModel[] {
  const byLabel = new Map<string, SourceStatus>();
  const fixes = new Map<string, SourceFix | null>();
  for (const input of inputs) fixes.set(input.label, worstFix(fixes.get(input.label), inputFix(input)));
  for (const status of inputs.map(describeSource)) {
    const previous = byLabel.get(status.label);
    if (!previous) {
      byLabel.set(status.label, status);
      continue;
    }
    const worst = STATE_RANK[status.state] > STATE_RANK[previous.state] ? status : previous;
    byLabel.set(status.label, {
      label: status.label,
      state: worst.state,
      problem: Array.from(new Set([previous.problem, status.problem].filter(Boolean))).join(', '),
      details: Array.from(new Set([...previous.details, ...status.details])),
    });
  }
  const statuses = Array.from(byLabel.values());
  const healthy = statuses.filter((status) => status.state === 'ok');
  const chips: SourceChipModel[] = statuses
    .filter((status) => status.state !== 'ok')
    .sort((left, right) => STATE_RANK[right.state] - STATE_RANK[left.state])
    .map((status) => ({
      key: status.label,
      state: status.state,
      text: status.problem ? `${status.label} · ${status.problem}` : status.label,
      details: status.details,
      fix: fixes.get(status.label) || null,
    }));
  if (healthy.length) {
    chips.push({
      key: 'healthy',
      state: 'ok',
      text: healthy.map((status) => status.label).join(' · '),
      details: healthy.flatMap((status) => status.details),
    });
  }
  return chips;
}

/* ───────────────────────── Dashboard helpers ───────────────────────── */

type FinancialMetrics = Record<string, unknown> & {
  coverage?: Record<string, unknown>;
  sources?: Record<string, unknown>;
};

export function financialMetricsOf(data: DashboardPayload | undefined): FinancialMetrics {
  const raw = (data as Record<string, unknown> | undefined)?.financial_metrics;
  return raw && typeof raw === 'object' ? (raw as FinancialMetrics) : {};
}

export function optionalNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const number = toNumber(value);
  return Number.isFinite(number) ? number : null;
}

export function closedMonthNumber(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) return Math.max(0, Math.min(12, Math.round(value)));
  const raw = safeString(value).trim();
  const yearMonth = raw.match(/^\d{4}-(\d{2})/);
  const parsed = Number(yearMonth?.[1] || raw);
  return Number.isFinite(parsed) ? Math.max(0, Math.min(12, Math.round(parsed))) : 0;
}

function capitalize(text: string): string {
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : text;
}

/** « janvier → août » for closed months 1..n. */
export function closedRangeLabel(year: number, closed: number): string {
  if (closed <= 1) return monthName(`${year}-01`);
  return `${monthName(`${year}-01`)} → ${monthName(`${year}-${pad(closed)}`)}`;
}

/**
 * True when the exercise has closed months but not a single euro of realised
 * production for them in Fibery: the produced revenue is then « not entered »,
 * never a real zero.
 */
export function realisedMissing(data: DashboardPayload | undefined, year: number): boolean {
  if (!data) return false;
  const record = data as Record<string, unknown>;
  const metrics = financialMetricsOf(data);
  const productionAvailable = (metrics.coverage as Record<string, unknown> | undefined)?.production_available !== false
    && ('ca_produit_ytd' in record || 'produced_revenue' in metrics);
  if (!productionAvailable) return false;
  const closed = closedMonthNumber(record.last_closed_month);
  if (closed <= 0) return false;
  const realised = (record.monthly_realise || {}) as Record<string, unknown>;
  const closedTotal = Array.from({ length: closed }, (_, index) => toNumber(realised[`${year}-${pad(index + 1)}`]))
    .reduce((sum, value) => sum + value, 0);
  const produced = optionalNumber(metrics.produced_revenue) ?? optionalNumber(record.ca_produit_ytd) ?? 0;
  return closedTotal === 0 && produced === 0;
}

/* ───────────────────────── Charge et capacité ───────────────────────── */

export interface LoadMonth {
  month: string;
  short: string;
  load: number;
  subcontracted: number;
  capacity: number;
  /** Claire's congés / formations of the month, already taken off the capacity. */
  absence: number;
  remaining: number;
  overload: number;
  phase: 'past' | 'current' | 'future';
}

export function buildLoadMonths(data: DashboardPayload | undefined, today: Date): LoadMonth[] {
  const rows = Array.isArray(data?.monthly_load_summary) ? data.monthly_load_summary : [];
  const currentMonth = monthKeyOf(today);
  return rows
    .filter((row) => /^\d{4}-\d{2}$/.test(safeString(row.month)))
    .map((row) => {
      const month = safeString(row.month);
      return {
        month,
        short: monthShort(month),
        load: toNumber(row.claire_days),
        subcontracted: toNumber(row.subcontracted_days),
        capacity: toNumber(row.capacity),
        absence: Math.max(0, toNumber((row as Record<string, unknown>).absence_days)),
        remaining: Math.max(0, toNumber(row.remaining)),
        overload: Math.max(0, toNumber(row.overload)),
        phase: month < currentMonth ? 'past' as const : month === currentMonth ? 'current' as const : 'future' as const,
      };
    })
    .sort((left, right) => left.month.localeCompare(right.month));
}

/* ───────────────────────── À traiter ───────────────────────── */

export interface TodoAction {
  key: string;
  tone: Tone;
  title: string;
  detail?: string;
  /** Verb of the single button; the button opens or executes THIS item. */
  actionLabel: string;
  target: NavTarget | null;
  /** The button runs the synchronisation of this connector (SyncSourceButton); `target` is then THAT connector. */
  sync?: SourceConnector;
}

function leadingCount(text: unknown): number {
  const match = safeString(text).match(/(\d+)/);
  return match ? Number(match[1]) : 0;
}

function sectionOf(payload: MyWeekPayload | undefined, key: string) {
  return (payload?.sections || []).find((section) => section.key === key);
}

/**
 * Keys already answered by a state of the week view with its own button (one
 * button per intent): the deals panel (HubSpot), the agenda (Digiforma) and
 * the « Créances à relancer » figure (Qonto not synchronised, or incomplete).
 */
const PANEL_OWNED_TRUST_KEYS = new Set(['hubspot-unavailable', 'digiforma-unavailable', 'qonto-unavailable', 'qonto-financial-completeness']);

const QONTO_CONNECTOR = toIntegrationsTarget({ connector: 'qonto' });

function trustAction(item: MyWeekItem, metrics: MyWeekPayload['metrics'], sources: MyWeekPayload['sources']): TodoAction | null {
  const key = safeString(item.key);
  const target = fromMyWeekTarget(item.target);
  if (PANEL_OWNED_TRUST_KEYS.has(key)) return null;
  if (key === 'data-quality') {
    const count = leadingCount(item.title);
    return {
      key,
      tone: 'error',
      title: count ? `Traiter ${count} anomalie${count > 1 ? 's' : ''} de données` : 'Traiter les anomalies de données',
      detail: safeString(item.meta) || undefined,
      actionLabel: 'Voir les anomalies',
      target,
    };
  }
  if (key === 'unassigned-sessions') {
    return { key, tone: 'signed', title: safeString(item.title), detail: 'Session sans ressource rattachée', actionLabel: 'Affecter dans le calendrier', target };
  }
  if (key.startsWith('hubspot-blocked')) {
    // The button opens exactly the deals to correct (?probleme=…), never the
    // whole pipeline filtered on another criterion; an unverified portal is
    // re-verified by a synchronisation, run right here.
    const task = hubspotReadinessTask(key.slice('hubspot-'.length), item.meta, sources?.pipeline);
    return {
      key,
      tone: 'pipeline',
      title: task.title,
      detail: task.detail,
      actionLabel: task.actionLabel,
      target: task.target,
      ...(task.sync ? { sync: task.sync } : {}),
    };
  }
  if (key.startsWith('hubspot-')) {
    return { key, tone: 'pipeline', title: safeString(item.title), detail: safeString(item.meta) || undefined, actionLabel: 'Ouvrir dans le pipeline', target };
  }
  if (key === 'qonto-mission-attribution') {
    const coverage = optionalNumber(metrics?.mission_billing_attribution_coverage_pct);
    return {
      key,
      tone: 'billed',
      title: 'Relier les factures Qonto aux missions',
      detail: coverage === null ? safeString(item.meta) || undefined : `Couverture par mission : ${Math.round(coverage).toLocaleString('fr-FR')} %`,
      actionLabel: 'Relier les factures',
      target: toIntegrationsTarget({ connector: 'qonto', linkType: 'client_invoice' }),
    };
  }
  if (key === 'qonto-mission-attribution-unavailable') {
    // The server title (« Attribution historique Qonto indisponible ») and its
    // meta (raw reason, possibly a technical error) are never printed: the row
    // says what to do and what is missing, in the operator's words.
    return {
      key,
      tone: 'billed',
      title: 'Rattacher les factures Qonto aux missions',
      detail: 'Rattachement par mission non calculé',
      actionLabel: 'Ouvrir le connecteur Qonto',
      target: QONTO_CONNECTOR,
    };
  }
  if (key.startsWith('qonto-')) {
    // A Qonto signal this screen does not know yet: its server title is not
    // reused (it may say « indisponible »); the button names its destination.
    return { key, tone: 'billed', title: 'Vérifier la source Qonto', detail: undefined, actionLabel: 'Ouvrir le connecteur Qonto', target: QONTO_CONNECTOR };
  }
  return { key, tone: 'muted', title: safeString(item.title), detail: safeString(item.meta) || undefined, actionLabel: 'Ouvrir', target };
}

function invoiceDetail(item: MyWeekItem, today: Date): string {
  const due = parseIsoDate(item.date);
  const dueLabel = due
    ? due < new Date(today.getFullYear(), today.getMonth(), today.getDate())
      ? `échue depuis le ${shortDate(item.date, today.getFullYear())}`
      : `échéance le ${shortDate(item.date, today.getFullYear())}`
    : 'échéance non renseignée';
  return [item.amount_label ? `${item.amount_label} HT` : '', dueLabel].filter(Boolean).join(' · ');
}

export interface TodoInput {
  dashboard: DashboardPayload | undefined;
  myWeek: MyWeekPayload | undefined;
  year: number;
  today: Date;
}

/**
 * The dashboard priorities rewritten as actions: each row names what to do
 * and its single button opens THAT item (an invoice, a month, a connector),
 * or a collective list whose label names it (« Voir les anomalies »).
 */
export function buildTodoActions({ dashboard, myWeek, year, today }: TodoInput): TodoAction[] {
  const actions: TodoAction[] = [];
  const record = (dashboard || {}) as Record<string, unknown>;

  if (realisedMissing(dashboard, year)) {
    const closed = closedMonthNumber(record.last_closed_month);
    actions.push({
      key: 'realised-missing',
      tone: 'produced',
      title: `Saisir le réalisé ${closed <= 1 ? 'de janvier' : closedRangeLabel(year, closed)}`,
      detail: 'Le CA produit reste à 0 € sans cette saisie',
      // The button executes: the planning opens with the paste panel, on January.
      actionLabel: 'Coller depuis Excel',
      target: toPlanningTarget({ monthKey: `${year}-01`, paste: true }),
    });
  }

  for (const item of sectionOf(myWeek, 'confiance')?.items || []) {
    const action = trustAction(item, myWeek?.metrics || {}, myWeek?.sources);
    if (action) actions.push(action);
  }

  const invoices = sectionOf(myWeek, 'creances_qonto');
  for (const item of invoices?.items || []) {
    actions.push({
      key: `invoice-${item.key}`,
      tone: item.tone === 'danger' ? 'alert' : 'billed',
      title: `Relancer ${item.title}`,
      detail: invoiceDetail(item, today),
      actionLabel: 'Ouvrir la facture',
      target: fromMyWeekItem(item),
    });
  }
  const invoiceTotal = toNumber(myWeek?.metrics?.invoices ?? invoices?.count);
  const hiddenInvoices = Math.max(0, invoiceTotal - (invoices?.items.length || 0));
  if (hiddenInvoices > 0) {
    actions.push({
      key: 'invoices-more',
      tone: 'billed',
      title: `Relancer ${hiddenInvoices} autre${hiddenInvoices > 1 ? 's' : ''} créance${hiddenInvoices > 1 ? 's' : ''}`,
      actionLabel: 'Voir les créances dans Factures',
      target: toFacturesTarget({ flag: 'overdue', scope: 'collection' }),
    });
  }

  for (const item of sectionOf(myWeek, 'brouillons_qonto')?.items || []) {
    actions.push({
      key: `draft-${item.key}`,
      tone: 'billed',
      title: `Émettre ${item.title}`,
      detail: [item.amount_label ? `${item.amount_label} HT` : '', 'brouillon Qonto'].filter(Boolean).join(' · '),
      actionLabel: 'Ouvrir le brouillon',
      target: fromMyWeekItem(item),
    });
  }

  const currentMonth = monthKeyOf(today);
  for (const month of buildLoadMonths(dashboard, today)) {
    if (month.month < currentMonth || month.overload <= 0.01) continue;
    actions.push({
      key: `overload-${month.month}`,
      tone: 'produced',
      title: `Sous-traiter ${formatDays(month.overload)} j en ${monthName(month.month)}`,
      detail: `Claire chargée ${formatDays(month.load)} j pour ${formatDays(month.capacity)} j de capacité`,
      actionLabel: 'Trouver un intervenant',
      target: toIntervenantsTarget({
        monthKey: month.month,
        demandDays: String(Math.round(month.overload * 10) / 10),
        flag: 'subcontracting-demand',
      }),
    });
  }

  return actions;
}

/* ───────────────────────── Parcours de l'argent ───────────────────────── */

export type FlowKey = 'pipeline' | 'signed' | 'produced' | 'billed' | 'paid';

export interface FlowHonest {
  title: string;
  detail: string;
  /** The single action that fixes the stage (sync, deals or invoices to correct, paste). */
  action?: FixAction;
}

export interface FlowStep {
  key: FlowKey;
  label: string;
  source: string;
  tone: Tone;
  value: number | null;
  detail?: string;
  /** Share of the year forecast carried by the bar (0..1), when meaningful. */
  share?: number;
  shareLabel?: string;
  info?: string;
  honest?: FlowHonest;
}

function sources(metrics: FinancialMetrics): Record<string, unknown> {
  return (metrics.sources || {}) as Record<string, unknown>;
}

function sourceMeta(metrics: FinancialMetrics, key: string): SourceMetadata {
  return (asMeta(sources(metrics)[key]) || {}) as SourceMetadata;
}

const PROBLEM_TITLE: Record<PipelineProblem, string> = {
  'hors-exercice': 'Dates de clôture à corriger',
  'sans-date': 'Dates de clôture à compléter',
  'sans-montant': 'Montants à compléter',
  devise: 'Devise à compléter',
  'sans-probabilite': 'Probabilités à compléter',
};

function problemOfTarget(action: FixAction | null): PipelineProblem | null {
  if (!action || action.kind !== 'navigate') return null;
  const match = /[?&]probleme=([a-z-]+)/.exec(action.target.route);
  return match && match[1] in PROBLEM_TITLE ? (match[1] as PipelineProblem) : null;
}

/**
 * Why a HubSpot stage (pipeline, acquis) has no figure: its real cause, read
 * from the source copy, and the action that fixes it — synchronise HubSpot,
 * or open exactly the deals to correct (`/pipeline?probleme=…`).
 */
function hubspotHonest(metrics: FinancialMetrics, year: number, what: string): FlowHonest {
  const meta = sourceMeta(metrics, 'pipeline');
  const coverage = (metrics.coverage || {}) as Record<string, unknown>;
  const outside = optionalNumber(coverage.hubspot_open_deals_outside_year_count);
  const diagnosis = diagnoseSource('hubspot', sources(metrics).pipeline, { year, outsideYearCount: outside });
  if (diagnosis.cause === 'coverage_incomplete') {
    const problem = problemOfTarget(diagnosis.action);
    return { title: problem ? PROBLEM_TITLE[problem] : sourceStateTitle('hubspot', diagnosis), detail: diagnosis.reason, action: diagnosis.action || undefined };
  }
  if (diagnosis.cause) {
    return { title: sourceStateTitle('hubspot', diagnosis), detail: diagnosis.reason, action: diagnosis.action || undefined };
  }
  if (outside !== null && outside > 0) {
    const plural = outside > 1;
    return {
      title: PROBLEM_TITLE['hors-exercice'],
      detail: `${outside} affaire${plural ? 's' : ''} ouverte${plural ? 's' : ''} datée${plural ? 's' : ''} hors ${year}`,
      action: { kind: 'navigate', label: plural ? `Ouvrir les ${outside}` : 'Ouvrir l’affaire', target: pipelineProblemTarget('hors-exercice') },
    };
  }
  // The copy is up to date and complete, yet the server computed no figure:
  // said as such, with the connector where its detail is read.
  return {
    title: `${capitalize(what)} non calculé`,
    detail: safeString(meta.quality_warning) || `HubSpot est à jour mais n’a pas fourni ${what === 'acquis' ? 'l’acquis' : 'le pipeline'} de ${year}.`,
    action: { kind: 'navigate', label: 'Ouvrir le connecteur HubSpot', target: toIntegrationsTarget({ connector: 'hubspot' }) },
  };
}

/** Same for a Qonto stage (facturé, réglé): synchronise Qonto, or open the incomplete invoices. */
function qontoHonest(metrics: FinancialMetrics, what: string, year: number): FlowHonest {
  const meta = sourceMeta(metrics, 'invoices');
  const diagnosis = diagnoseSource('qonto', sources(metrics).invoices, { year });
  if (diagnosis.cause) {
    return {
      title: diagnosis.cause === 'coverage_incomplete' ? `${capitalize(what)} masqué` : sourceStateTitle('qonto', diagnosis),
      detail: diagnosis.reason,
      action: diagnosis.action || undefined,
    };
  }
  return {
    title: `${capitalize(what)} masqué`,
    detail: safeString(meta.quality_warning) || 'Des factures Qonto de l’exercice sont incomplètes : le total est masqué.',
    action: { kind: 'navigate', label: 'Voir les factures incomplètes', target: incompleteInvoicesTarget() },
  };
}

function shareOf(value: number | null, forecast: number | null): number | undefined {
  if (value === null || forecast === null || forecast <= 0) return undefined;
  return value / forecast;
}

function pctLabel(share: number | undefined): string {
  return share === undefined ? '' : `${Math.round(share * 100).toLocaleString('fr-FR')} % du prévu`;
}

export function invoiceCount(metrics: FinancialMetrics): number | null {
  const counts = metrics.invoice_status_counts;
  if (!counts || typeof counts !== 'object') return null;
  return Object.entries(counts as Record<string, unknown>)
    .filter(([status]) => !['draft', 'brouillon', 'canceled', 'cancelled', 'annulee'].includes(status.trim().toLowerCase()))
    .reduce((sum, [, value]) => sum + toNumber(value), 0);
}

/**
 * The five money stages of the year, always in the same colours. A stage
 * whose figure is absent becomes an honest state (what is missing + the next
 * click) instead of « Non disponible »; a button already offered by an earlier
 * stage is not repeated.
 */
export function buildMoneyFlow(data: DashboardPayload | undefined, year: number): FlowStep[] {
  const metrics = financialMetricsOf(data);
  const record = (data || {}) as Record<string, unknown>;
  const forecast = optionalNumber(metrics.year_revenue_forecast);

  const gross = optionalNumber(metrics.year_revenue_open_gross) ?? optionalNumber(metrics.pipeline_gross);
  const weighted = optionalNumber(metrics.year_revenue_open_weighted) ?? optionalNumber(metrics.pipeline_weighted);
  const amountCoverage = optionalNumber(metrics.pipeline_amount_coverage_pct);
  const pipelineShare = shareOf(weighted, forecast);
  const pipeline: FlowStep = gross === null
    ? { key: 'pipeline', label: 'Pipeline ouvert', source: 'HubSpot', tone: 'pipeline', value: null, honest: hubspotHonest(metrics, year, 'pipeline') }
    : {
      key: 'pipeline',
      label: 'Pipeline ouvert',
      source: 'HubSpot',
      tone: 'pipeline',
      value: gross,
      detail: [
        'Brut',
        weighted === null ? '' : `${formatCurrency(weighted)} pondéré`,
        amountCoverage !== null && amountCoverage < 100 ? `montants ${amountCoverage.toLocaleString('fr-FR')} %` : '',
      ].filter(Boolean).join(' · '),
      share: pipelineShare,
      shareLabel: pipelineShare === undefined ? undefined : `Pondéré : ${pctLabel(pipelineShare)}`,
      info: `Part ${year} des affaires ouvertes dans HubSpot. Pondéré = montant × probabilité.`,
    };

  const signedValue = optionalNumber(metrics.year_revenue_signed);
  const signedShare = shareOf(signedValue, forecast);
  const signed: FlowStep = signedValue === null
    ? { key: 'signed', label: 'Acquis', source: 'HubSpot', tone: 'signed', value: null, honest: hubspotHonest(metrics, year, 'acquis') }
    : {
      key: 'signed',
      label: 'Acquis',
      source: 'HubSpot',
      tone: 'signed',
      value: signedValue,
      detail: `Part ${year} des affaires acquises`,
      share: signedShare,
      shareLabel: pctLabel(signedShare),
      info: `Une affaire signée avant ${year} compte pour sa part ${year} ; une affaire livrée en ${year + 1} n'y figure pas.`,
    };

  const closed = closedMonthNumber(record.last_closed_month);
  const producedValue = optionalNumber(metrics.produced_revenue) ?? optionalNumber(record.ca_produit_ytd);
  const planning: FlowHonest['action'] = { kind: 'navigate', label: 'Ouvrir la planification', target: toPlanningTarget({ monthKey: `${year}-01` }) };
  const paste: FlowHonest['action'] = { kind: 'navigate', label: 'Coller depuis Excel', target: toPlanningTarget({ monthKey: `${year}-01`, paste: true }) };
  let produced: FlowStep;
  if (producedValue === null) {
    produced = { key: 'produced', label: 'Produit', source: 'Fibery', tone: 'produced', value: null, honest: { title: 'Production non chargée', detail: 'Le réalisé Fibery n’a pas pu être lu', action: planning } };
  } else if (realisedMissing(data, year)) {
    produced = {
      key: 'produced',
      label: 'Produit',
      source: 'Fibery',
      tone: 'produced',
      value: null,
      honest: { title: 'Réalisé non saisi', detail: `${capitalize(closedRangeLabel(year, closed))} : aucun CA réalisé`, action: paste },
    };
  } else if (closed <= 0 && producedValue === 0) {
    produced = { key: 'produced', label: 'Produit', source: 'Fibery', tone: 'produced', value: null, honest: { title: `Aucun mois clos en ${year}`, detail: 'Le réalisé se lit à la clôture de janvier' } };
  } else {
    const producedShare = shareOf(producedValue, forecast);
    produced = {
      key: 'produced',
      label: 'Produit',
      source: 'Fibery',
      tone: 'produced',
      value: producedValue,
      detail: `${capitalize(closedRangeLabel(year, closed))} · ${closed} mois clos`,
      share: producedShare,
      shareLabel: pctLabel(producedShare),
    };
  }

  const billedValue = optionalNumber(metrics.billed_revenue_ht);
  const billedShare = shareOf(billedValue, forecast);
  const count = invoiceCount(metrics);
  const billed: FlowStep = billedValue === null
    ? { key: 'billed', label: 'Facturé HT', source: 'Qonto', tone: 'billed', value: null, honest: qontoHonest(metrics, 'facturé', year) }
    : {
      key: 'billed',
      label: 'Facturé HT',
      source: 'Qonto',
      tone: 'billed',
      value: billedValue,
      detail: count === null ? `Factures émises en ${year}` : `${count} facture${count > 1 ? 's' : ''} émise${count > 1 ? 's' : ''} en ${year}`,
      share: billedShare,
      shareLabel: pctLabel(billedShare),
    };

  const paidValue = optionalNumber(metrics.paid_revenue_ht);
  const cash = optionalNumber(metrics.cash_collected);
  const paidShare = shareOf(paidValue, forecast);
  const paid: FlowStep = paidValue === null
    ? { key: 'paid', label: 'Réglé HT', source: 'Qonto', tone: 'paid', value: null, honest: qontoHonest(metrics, 'réglé', year) }
    : {
      key: 'paid',
      label: 'Réglé HT',
      source: 'Qonto',
      tone: 'paid',
      value: paidValue,
      detail: cash === null ? `Factures ${year}, payé à date` : `${formatCurrency(cash)} TTC sur factures ${year}`,
      share: paidShare,
      shareLabel: pctLabel(paidShare),
      info: `${PAID_REVENUE_HT_LABEL} : ${paidRevenueHtScope(year)}.`,
    };

  const steps = [pipeline, signed, produced, billed, paid];
  const seen = new Map<string, string>();
  for (const step of steps) {
    const action = step.honest?.action;
    if (!step.honest || !action) continue;
    const intent = action.kind === 'sync'
      ? `sync|${action.connector}`
      : action.kind === 'external'
        ? `external|${action.href}`
        : `${action.label}|${action.target.route}|${JSON.stringify(action.target.filters || {})}`;
    const owner = seen.get(intent);
    if (owner) {
      step.honest = { title: step.honest.title, detail: `Même correction que « ${owner} »` };
    } else {
      seen.set(intent, step.label);
    }
  }
  return steps;
}

/* ───────────────────────── Clients et catégories ───────────────────────── */

export interface TopClient {
  id: string;
  name: string;
  amount: number;
  missions: number;
  sessions: number;
  /**
   * The HubSpot link needing action, read from `hubspot_link_reason` with the
   * labels of the Clients list (Non lié, Nom différent, Lien incohérent, En
   * attente); null when linked, or when the portal is not verified.
   */
  hubspot: IdentityChipState | null;
  /** Same-origin logo of the client, '' when the server gave none. */
  logoUrl: string;
}

export function buildTopClients(rows: TabEntity[], limit = 5): { clients: TopClient[]; over10k: number } {
  const withRevenue = rows
    .map((row) => ({
      id: safeString(row['fibery/id'] || row.id),
      name: safeString(row.name).trim() || 'Client sans nom',
      amount: toNumber(row.ca_prevu),
      missions: toNumber(row.nb_missions),
      sessions: toNumber(row.nb_sessions),
      hubspot: clientHubspotAttention(row),
      logoUrl: clientLogoSrc(row),
    }))
    .filter((row) => row.amount > 0)
    .sort((left, right) => right.amount - left.amount);
  return { clients: withRevenue.slice(0, limit), over10k: withRevenue.filter((row) => row.amount > 10_000).length };
}

export interface CategoryShare {
  key: string;
  label: string;
  amount: number;
  share: number;
  uncategorized: boolean;
}

const UNCATEGORIZED_KEYS = new Set(['', 'noncategorise', 'nondefini', 'aucune', 'none']);

function simpleKey(raw: string): string {
  return raw.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '');
}

export function buildCategories(raw: unknown): CategoryShare[] {
  if (!raw || typeof raw !== 'object') return [];
  // Every « no category » spelling (empty, « Non categorise », « None »…) is one line.
  const merged = new Map<string, number>();
  Object.entries(raw as Record<string, unknown>).forEach(([key, value]) => {
    const target = UNCATEGORIZED_KEYS.has(simpleKey(key)) ? '' : key;
    merged.set(target, (merged.get(target) ?? 0) + toNumber(value));
  });
  const entries = Array.from(merged, ([key, amount]) => ({ key, amount }))
    .filter((entry) => entry.amount > 0);
  const total = entries.reduce((sum, entry) => sum + entry.amount, 0);
  if (total <= 0) return [];
  return entries
    .map((entry) => {
      const uncategorized = UNCATEGORIZED_KEYS.has(simpleKey(entry.key));
      return {
        key: entry.key,
        label: uncategorized ? 'Non catégorisé' : entry.key,
        amount: entry.amount,
        share: entry.amount / total,
        uncategorized,
      };
    })
    .sort((left, right) => Number(left.uncategorized) - Number(right.uncategorized) || right.amount - left.amount);
}

/* ───────────────────────── Agenda du mois ───────────────────────── */

export type AgendaKind = 'formation' | 'session' | 'event';

export interface AgendaEntry {
  id: string;
  title: string;
  kind: AgendaKind;
  typeLabel: string;
  start: Date;
  end: Date;
  resource: string;
  mission: string;
  /** The mission's client, or a Digiforma slot's single company (its logo). */
  client: ItemClient | null;
}

function localDay(raw: unknown): Date | null {
  const text = safeString(raw).trim();
  if (!text) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return parseIsoDate(text);
  const parsed = new Date(text);
  if (Number.isNaN(parsed.getTime())) return parseIsoDate(text);
  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
}

/**
 * Dated sessions and events of the calendar payload. Provisional month-level
 * affectations (`production`) carry no real date and are left out: placing
 * them on the 1st would date a fact the data does not support.
 */
export function parseAgendaEntries(payload: { entities?: unknown } | undefined): AgendaEntry[] {
  const rows = Array.isArray(payload?.entities) ? (payload.entities as Array<Record<string, unknown>>) : [];
  const entries: AgendaEntry[] = [];
  for (const row of rows) {
    const sourceSystem = safeString(row.source_system).toLowerCase();
    const entityType = safeString(row.entity_type) || (sourceSystem === 'digiforma' ? 'sessions' : '');
    if (entityType !== 'sessions' && entityType !== 'calendar_events') continue;
    const start = localDay(row.start || row.date_debut);
    if (!start) continue;
    const endRaw = localDay(row.end || row.date_fin);
    const end = endRaw && endRaw >= start ? endRaw : start;
    const typeLabel = safeString(row.type || row.type_session).trim();
    const isTraining = sourceSystem === 'digiforma' || simpleKey(typeLabel).includes('formation');
    entries.push({
      id: safeString(row['fibery/id'] || row.id || row.external_id),
      title: safeString(row.title || row.name).trim() || 'Session',
      kind: entityType === 'calendar_events' ? 'event' : isTraining ? 'formation' : 'session',
      typeLabel: typeLabel || (entityType === 'calendar_events' ? 'Évènement' : isTraining ? 'Formation' : 'Session'),
      start,
      end,
      resource: safeString(row.resource_name).trim(),
      mission: safeString(row.mission_name).trim(),
      client: calendarRowClient(row),
    });
  }
  return entries.sort((left, right) => left.start.getTime() - right.start.getTime() || right.end.getTime() - left.end.getTime());
}

export function overlapsRange(entry: AgendaEntry, from: Date, to: Date): boolean {
  return entry.start <= to && entry.end >= from;
}

/** Beyond two weeks an entry would repeat on every row of the month grid. */
export const LONG_ENTRY_DAYS = 14;

/** Calendar days covered by an entry, both ends included. */
export function entryDays(entry: AgendaEntry): number {
  return Math.round((entry.end.getTime() - entry.start.getTime()) / 86_400_000) + 1;
}

/** The calendar item an agenda entry opens, or `null` when it has no identity. */
export function agendaEntryTarget(entry: AgendaEntry): NavTarget | null {
  return toSessionTarget(entry.id, isoDate(entry.start));
}

/**
 * Splits the month agenda. Entries covering more than 14 days (an individual
 * coaching from January to October…) leave the grid, where they would repeat
 * on every week, for the single « En cours tout le mois » line: the ones
 * that touch the month, in start order. The grid keeps the short entries.
 */
export function splitMonthAgenda(entries: AgendaEntry[], monthKey: string): { ongoing: AgendaEntry[]; short: AgendaEntry[] } {
  const first = monthDate(monthKey);
  const last = new Date(first.getFullYear(), first.getMonth() + 1, 0);
  const ongoing: AgendaEntry[] = [];
  const short: AgendaEntry[] = [];
  for (const entry of entries) {
    if (entryDays(entry) <= LONG_ENTRY_DAYS) short.push(entry);
    else if (overlapsRange(entry, first, last)) ongoing.push(entry);
  }
  return { ongoing, short };
}

export interface OngoingGroup {
  key: string;
  kind: AgendaKind;
  /** Shared part of the titles (the whole title for a lone entry). */
  label: string;
  /** « du 12/01 au 30/10 », common to every entry of the group. */
  range: string;
  /** Each entry keeps its own button; `short` is what tells it apart. */
  entries: Array<{ entry: AgendaEntry; short: string }>;
}

const TITLE_SEPARATORS = /[\s–—\-·:,/(]+$/;
const LEADING_SEPARATORS = /^[\s–—\-·:,/)]+/;

/** Common start of the titles, cut back to a word boundary. */
function commonTitlePrefix(titles: string[]): string {
  let prefix = titles[0] || '';
  for (const title of titles.slice(1)) {
    let index = 0;
    while (index < prefix.length && index < title.length && prefix[index] === title[index]) index += 1;
    prefix = prefix.slice(0, index);
  }
  const atBoundary = titles.every((title) => title.length === prefix.length || /[\s–—\-·:,/(]/.test(title[prefix.length]));
  if (!atBoundary) prefix = prefix.replace(/[^\s–—\-·:,/(]+$/, '');
  return prefix;
}

/**
 * Entries of the « En cours tout le mois » line sharing their dates and the
 * start of their title (five individual coachings « … – BT », « … – MM »…)
 * read as one mention followed by what tells them apart, as in the validated
 * mockup; each entry keeps its own button. A lone entry stays whole.
 */
export function groupOngoingEntries(entries: AgendaEntry[]): OngoingGroup[] {
  const buckets = new Map<string, AgendaEntry[]>();
  for (const entry of entries) {
    const key = `${entry.kind}|${isoDate(entry.start)}|${isoDate(entry.end)}`;
    buckets.set(key, [...(buckets.get(key) || []), entry]);
  }
  const groups: OngoingGroup[] = [];
  for (const [key, bucket] of buckets) {
    const range = formatDateRangeShort(bucket[0]);
    const prefix = bucket.length > 1 ? commonTitlePrefix(bucket.map((entry) => entry.title)) : '';
    const label = prefix.replace(TITLE_SEPARATORS, '').trim();
    const shorts = bucket.map((entry) => entry.title.slice(prefix.length).replace(LEADING_SEPARATORS, '').replace(/^([^(]*)\)$/, '$1').trim());
    if (bucket.length > 1 && label.length >= 4 && shorts.every(Boolean)) {
      groups.push({ key, kind: bucket[0].kind, label, range, entries: bucket.map((entry, index) => ({ entry, short: shorts[index] })) });
      continue;
    }
    for (const entry of bucket) {
      groups.push({ key: `${key}|${entry.id || entry.title}`, kind: entry.kind, label: entry.title, range, entries: [{ entry, short: entry.title }] });
    }
  }
  return groups;
}

export interface WeekSegment {
  entry: AgendaEntry;
  col: number;
  span: number;
  lane: number;
  continuesBefore: boolean;
  continuesAfter: boolean;
}

/** Packs the entries of one grid week into lanes; beyond `maxLanes` they are counted, not drawn. */
export function layoutWeek(entries: AgendaEntry[], week: GridDay[], maxLanes = 3): { segments: WeekSegment[]; hidden: number } {
  const weekStart = parseIsoDate(week[0].iso) as Date;
  const weekEnd = parseIsoDate(week[week.length - 1].iso) as Date;
  const laneEnds: number[] = [];
  const segments: WeekSegment[] = [];
  let hidden = 0;
  const dayIndex = (date: Date) => Math.round((date.getTime() - weekStart.getTime()) / 86_400_000);
  const candidates = entries
    .filter((entry) => overlapsRange(entry, weekStart, weekEnd))
    .map((entry) => {
      const from = entry.start < weekStart ? weekStart : entry.start;
      const to = entry.end > weekEnd ? weekEnd : entry.end;
      return { entry, first: dayIndex(from), last: dayIndex(to) };
    })
    .sort((left, right) => left.first - right.first || (right.last - right.first) - (left.last - left.first));
  for (const candidate of candidates) {
    let lane = laneEnds.findIndex((end) => end < candidate.first);
    if (lane === -1) lane = laneEnds.length;
    if (lane >= maxLanes) {
      hidden += 1;
      continue;
    }
    laneEnds[lane] = candidate.last;
    segments.push({
      entry: candidate.entry,
      col: candidate.first + 1,
      span: candidate.last - candidate.first + 1,
      lane,
      continuesBefore: candidate.entry.start < weekStart,
      continuesAfter: candidate.entry.end > weekEnd,
    });
  }
  return { segments, hidden };
}

/** « 4 formation · 1 diagnostic » for the dated sessions of a month. */
export function sessionTypeSummary(entries: AgendaEntry[], limit = 3): string {
  const counts = new Map<string, number>();
  for (const entry of entries) {
    const label = entry.typeLabel.toLocaleLowerCase('fr-FR');
    counts.set(label, (counts.get(label) || 0) + 1);
  }
  return Array.from(counts.entries())
    .sort((left, right) => right[1] - left[1])
    .slice(0, limit)
    .map(([label, count]) => `${count} ${label}`)
    .join(' · ');
}

export function formatDateRangeShort(entry: AgendaEntry): string {
  const format = (date: Date) => date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
  return entry.start.getTime() === entry.end.getTime() ? `le ${format(entry.start)}` : `du ${format(entry.start)} au ${format(entry.end)}`;
}

/* ───────────────────────── Factures à envoyer (ADR 0005) ───────────────────────── */

export type PlannedInvoiceState = 'to_plan' | 'ready' | 'draft' | 'stale';

export interface InvoiceToSend {
  missionId: string;
  missionName: string;
  clientName: string;
  date: string;
  amount: number;
  state: PlannedInvoiceState;
  overdue: boolean;
}

/** The observed period of the dashboard view, as ISO dates (inclusive). */
export function dashboardPeriod(view: DashboardView, year: number, weekStart: string, monthKey: string): { from: string; to: string } {
  if (view === 'semaine') {
    const start = parseIsoDate(weekStart) || new Date(year, 0, 1);
    return { from: isoDate(start), to: isoDate(addDays(start, 6)) };
  }
  if (view === 'mois' && /^\d{4}-\d{2}$/.test(monthKey)) {
    const [y, m] = monthKey.split('-').map(Number);
    return { from: `${monthKey}-01`, to: isoDate(new Date(y, m, 0)) };
  }
  return { from: `${year}-01-01`, to: `${year}-12-31` };
}

/**
 * Invoices to send in the period (Pascal, 05/10/2026 : « dans le dashboard
 * principal, elle ait le rappel des factures à envoyer sur la période
 * observée »), with the ones already late first: a forgotten invoice matters.
 */
export function invoicesToSendInPeriod(data: DashboardPayload | undefined, period: { from: string; to: string }, today: Date): InvoiceToSend[] {
  const raw = (data as unknown as Record<string, unknown> | undefined)?.invoices_to_send;
  if (!Array.isArray(raw)) return [];
  const todayIso = isoDate(today);
  return raw
    .map((item) => item as Record<string, unknown>)
    .map((item) => ({
      missionId: safeString(item.mission_id),
      missionName: safeString(item.mission_name) || 'Mission',
      clientName: safeString(item.client_name),
      date: safeString(item.date).slice(0, 10),
      amount: toNumber(item.amount_ht),
      state: (safeString(item.state) || 'to_plan') as PlannedInvoiceState,
      overdue: false,
    }))
    .filter((item) => item.date && item.date <= period.to && (item.date >= period.from || item.date < todayIso))
    .map((item) => ({ ...item, overdue: item.date < todayIso }))
    .sort((left, right) => left.date.localeCompare(right.date) || left.missionName.localeCompare(right.missionName, 'fr'));
}
